import { ShieldOff, AlertTriangle, Inbox, Loader2 } from 'lucide-react';

/**
 * Les quatre etats d'un ecran qui attend des donnees, dits SEPAREMENT.
 *
 * LE DEFAUT QU'IL FERME (item I3 du lot I)
 *
 * `FacturationPage` faisait exactement ceci :
 *
 *     try { const r = await api.get('/factures'); setFactures(r.data || []); }
 *     catch { setFactures([]); }
 *
 * Un refus de droits, une panne du serveur et un parc reellement vide y produisaient la
 * MEME phrase : « Aucune facture emise ». Trois situations qui appellent trois actions
 * differentes -- demander un acces, prevenir l'equipe, ou ne rien faire -- reduites a
 * une seule, la plus rassurante des trois. Trois autres ecrans echouaient de trois
 * facons encore differentes.
 *
 * La regle depasse ce lot : **un ecran ne presente jamais un refus comme un resultat
 * vide.** Elle n'est tenable que si les messages vivent a UN endroit ; ecrits ecran par
 * ecran, le quatrieme se trompera.
 *
 *     403        « vous n'avez pas acces »        -> une question de droits
 *     4xx / 5xx  « erreur »                       -> une panne, on peut reessayer
 *     liste vide « aucune donnee »                -> tout va bien, il n'y a rien
 *     en cours   un indicateur d'attente          -> ne rien affirmer trop tot
 *
 * USAGE
 *
 *     const [etat, setEtat] = useState({ chargement: true });
 *     try { setEtat({ donnees: (await api.get('/factures')).data || [] }); }
 *     catch (e) { setEtat({ erreur: e }); }
 *     ...
 *     <EtatEcran etat={etat} nom="facture" onReessayer={charger}>
 *       {(factures) => <Tableau lignes={factures} />}
 *     </EtatEcran>
 */

/** Le statut HTTP d'une erreur axios, ou null si la requete n'a jamais abouti. */
export const statutDe = (erreur) => erreur?.response?.status ?? null;

/** Un refus de droits se reconnait au 403, et a lui seul. Le 401 est traite ailleurs :
 *  l'intercepteur d'axios renvoie deja vers la connexion. */
export const estUnRefus = (erreur) => statutDe(erreur) === 403;

const Cadre = ({ icone: Icone, ton, titre, detail, action }) => (
  <div
    className="flex flex-col items-center justify-center gap-2 py-12 px-6 text-center"
    data-testid={'etat-ecran-' + ton}
    role={ton === 'erreur' || ton === 'refus' ? 'alert' : undefined}
  >
    <Icone className={`w-8 h-8 ${ton === 'refus' ? 'text-amber-500'
      : ton === 'erreur' ? 'text-red-500' : 'text-slate-300'}`} aria-hidden />
    <p className="text-[15px] font-medium text-slate-700">{titre}</p>
    {detail && <p className="text-[13px] text-slate-500 max-w-md">{detail}</p>}
    {action}
  </div>
);

export function EtatRefus({ quoi }) {
  return (
    <Cadre
      icone={ShieldOff}
      ton="refus"
      titre="Vous n'avez pas accès à cet écran"
      detail={`Votre profil ne permet pas de consulter ${quoi || 'ces données'}. `
        + "Demandez l'accès à l'administrateur de votre compte."}
    />
  );
}

export function EtatErreur({ onReessayer }) {
  return (
    <Cadre
      icone={AlertTriangle}
      ton="erreur"
      titre="Erreur lors du chargement"
      detail="Les données n'ont pas pu être récupérées. Ce n'est pas un problème de droits."
      action={onReessayer ? (
        <button
          type="button"
          onClick={onReessayer}
          className="mt-1 text-[13px] font-medium text-emerald-700 hover:underline"
          data-testid="etat-ecran-reessayer"
        >
          Réessayer
        </button>
      ) : null}
    />
  );
}

export function EtatVide({ quoi }) {
  return (
    <Cadre icone={Inbox} ton="vide" titre={`Aucune donnée${quoi ? ' : ' + quoi : ''}`}
           detail="Il n'y a rien à afficher pour le moment." />
  );
}

export function EtatChargement() {
  return (
    <div className="flex items-center justify-center py-12" data-testid="etat-ecran-chargement">
      <Loader2 className="w-5 h-5 animate-spin text-slate-400" aria-label="Chargement" />
    </div>
  );
}

/**
 * L'aiguillage. `etat` porte au plus une des quatre formes :
 * `{ chargement: true }` · `{ erreur }` · `{ donnees }`.
 */
export default function EtatEcran({ etat, quoi, onReessayer, children }) {
  if (etat?.chargement) return <EtatChargement />;
  if (etat?.erreur) {
    return estUnRefus(etat.erreur)
      ? <EtatRefus quoi={quoi} />
      : <EtatErreur onReessayer={onReessayer} />;
  }
  const donnees = etat?.donnees;
  if (Array.isArray(donnees) && donnees.length === 0) return <EtatVide quoi={quoi} />;
  return typeof children === 'function' ? children(donnees) : children;
}
